/**
 * GuestUploadModal Component
 *
 * Modal for guests to upload photos or videos directly from the gallery page.
 * Unified experience for viewing and contributing to the event.
 */

import { useState, useRef } from 'react';
import { X, Upload, Video, Image as ImageIcon, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { uploadVideo } from '../../hooks/useVideos';

const UPLOAD_STATES = {
  FORM: 'form',
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  ERROR: 'error'
};

// Max file sizes
const MAX_VIDEO_FILE_SIZE = 100 * 1024 * 1024;
const MAX_IMAGE_FILE_SIZE = 25 * 1024 * 1024;

// Max video duration: 3 minutes (180 seconds)
const MAX_DURATION_SECONDS = 180;

// Accepted video formats
const ACCEPTED_VIDEO_FORMATS = [
  'video/mp4',
  'video/webm',
  'video/quicktime', // MOV
  'video/x-msvideo', // AVI
  'video/x-matroska' // MKV
];

// Accepted image formats
const ACCEPTED_IMAGE_FORMATS = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif'
];

const isVideoFile = (file) => file?.type?.startsWith('video/');
const isImageFile = (file) => file?.type?.startsWith('image/');
const formatMb = (bytes) => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

// Validate video duration
const validateVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      const duration = Math.floor(video.duration);

      if (duration > MAX_DURATION_SECONDS) {
        const maxMinutes = Math.floor(MAX_DURATION_SECONDS / 60);
        reject(new Error(`Video must be ${maxMinutes} minutes or less. Your video is ${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, '0')}`));
      } else {
        resolve(duration);
      }
    };

    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error('Could not read video file. Please ensure it is a valid video.'));
    };

    video.src = URL.createObjectURL(file);
  });
};

function GuestUploadModal({ isOpen, onClose, eventId, eventName, onUploadSuccess }) {
  const fileInputRef = useRef(null);

  const [uploadState, setUploadState] = useState(UPLOAD_STATES.FORM);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [videoDuration, setVideoDuration] = useState(null);
  const [isValidatingDuration, setIsValidatingDuration] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const resetSelectedFile = () => {
    setSelectedFile(null);
    setVideoDuration(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Validate and store selected media
  const processSelectedFile = async (file) => {
    setFormErrors(prev => ({ ...prev, file: null }));
    resetSelectedFile();

    if (!file) return;

    // Validate file type
    const isAcceptedVideo = isVideoFile(file) && ACCEPTED_VIDEO_FORMATS.includes(file.type);
    const isAcceptedImage = isImageFile(file) && ACCEPTED_IMAGE_FORMATS.includes(file.type);
    if (!isAcceptedVideo && !isAcceptedImage) {
      setFormErrors(prev => ({
        ...prev,
        file: 'Please select a valid photo or video file (MP4, WebM, MOV, AVI, MKV, JPG, PNG, WEBP, or HEIC).'
      }));
      return;
    }

    // Validate file size
    const maxSize = isVideoFile(file) ? MAX_VIDEO_FILE_SIZE : MAX_IMAGE_FILE_SIZE;
    if (file.size > maxSize) {
      setFormErrors(prev => ({
        ...prev,
        file: `File size must be less than ${Math.floor(maxSize / (1024 * 1024))}MB`
      }));
      return;
    }

    if (isVideoFile(file)) {
      // Validate video duration
      setIsValidatingDuration(true);
      try {
        const duration = await validateVideoDuration(file);
        setVideoDuration(duration);
        setSelectedFile(file);
        setIsValidatingDuration(false);
      } catch (err) {
        setFormErrors(prev => ({
          ...prev,
          file: err.message
        }));
        setIsValidatingDuration(false);
        resetSelectedFile();
      }
      return;
    }

    setSelectedFile(file);
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    await processSelectedFile(file);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFile = e.dataTransfer?.files?.[0];
    await processSelectedFile(droppedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDragging) {
      setIsDragging(false);
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!guestName.trim()) {
      errors.name = 'Please enter your name';
    }

    if (!selectedFile) {
      errors.file = 'Please select a photo or video file';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle upload
  const handleUpload = async () => {
    if (!validateForm()) return;

    setUploadState(UPLOAD_STATES.UPLOADING);
    setUploadProgress(2);

    try {
      // Upload video
      const result = await uploadVideo(
        selectedFile,
        eventId,
        guestName.trim(),
        guestEmail.trim() || null,
        (nextProgress) => {
          setUploadProgress((prev) => Math.max(prev, nextProgress));
        }
      );

      setUploadProgress(100);

      if (result.success) {
        setUploadState(UPLOAD_STATES.SUCCESS);

        // Notify parent component of successful upload
        if (onUploadSuccess) {
          const newVideo = {
            id: result.id,
            videoUrl: result.url,
            guestName: guestName.trim(),
            guestEmail: guestEmail.trim() || null,
            eventId: eventId,
            fileSize: selectedFile.size,
            mimeType: selectedFile.type,
            mediaType: isImageFile(selectedFile) ? 'image' : 'video',
            uploadedAt: new Date(),
            status: 'active'
          };
          onUploadSuccess(newVideo);
        }
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setErrorMessage(error.message || 'Failed to upload file. Please try again.');
      setUploadState(UPLOAD_STATES.ERROR);
    }
  };

  // Reset form
  const handleReset = () => {
    setUploadState(UPLOAD_STATES.FORM);
    setGuestName('');
    setGuestEmail('');
    setSelectedFile(null);
    setVideoDuration(null);
    setIsDragging(false);
    setUploadProgress(0);
    setErrorMessage('');
    setFormErrors({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Close modal
  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-neutral-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-neutral-900 border-b border-white/10 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Share Photos & Videos</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Form State */}
          {uploadState === UPLOAD_STATES.FORM && (
            <div className="space-y-6">
              <p className="text-gray-300">
                Upload your photo or video to <span className="text-gold font-semibold">{eventName}</span>
              </p>

              {/* Name Input */}
              <div>
                <label htmlFor="guestName" className="block text-white font-medium mb-2">
                  Your Name <span className="text-gold">*</span>
                </label>
                <input
                  type="text"
                  id="guestName"
                  value={guestName}
                  onChange={(e) => {
                    setGuestName(e.target.value);
                    setFormErrors(prev => ({ ...prev, name: null }));
                  }}
                  className="w-full px-4 py-3 bg-white/10 border-2 border-white/20 text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400"
                  placeholder="Enter your name"
                  autoFocus
                />
                {formErrors.name && (
                  <p className="mt-2 text-crimson text-sm">{formErrors.name}</p>
                )}
              </div>

              {/* Email Input (Optional) */}
              <div>
                <label htmlFor="guestEmail" className="block text-white font-medium mb-2">
                  Email Address <span className="text-gray-400 text-sm">(Optional)</span>
                </label>
                <input
                  type="email"
                  id="guestEmail"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border-2 border-white/20 text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400"
                  placeholder="your.email@example.com"
                />
              </div>

              {/* File Picker */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Select Photo or Video <span className="text-gold">*</span>
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                    isDragging
                      ? 'border-gold bg-gold/15'
                      :
                    selectedFile
                      ? 'border-gold bg-gold/10'
                      : formErrors.file
                      ? 'border-crimson bg-crimson/10'
                      : 'border-white/30 bg-white/5 hover:border-gold hover:bg-gold/5'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*,image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  {selectedFile ? (
                    <div className="flex flex-col items-center gap-3">
                      {isImageFile(selectedFile) ? (
                        <ImageIcon className="w-10 h-10 text-gold" />
                      ) : (
                        <Video className="w-10 h-10 text-gold" />
                      )}
                      <div>
                        <p className="text-white font-semibold">{selectedFile.name}</p>
                        <p className="text-neutral-grey-400 text-sm mt-1">
                          {formatMb(selectedFile.size)}
                        </p>
                        {isVideoFile(selectedFile) && videoDuration && (
                          <p className="text-green-500 text-sm mt-1">
                            ✓ Duration: {Math.floor(videoDuration / 60)}:{String(videoDuration % 60).padStart(2, '0')} / 3:00 max
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          resetSelectedFile();
                        }}
                        className="text-sm text-gold hover:text-gold-light"
                      >
                        Change File
                      </button>
                    </div>
                  ) : isValidatingDuration ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-10 h-10 text-gold animate-spin" />
                      <div>
                        <p className="text-white font-semibold mb-1">
                          Validating video duration...
                        </p>
                        <p className="text-neutral-grey-400 text-sm">
                          Please wait
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <Upload className="w-10 h-10 text-neutral-grey-400" />
                      <div>
                        <p className="text-white font-semibold mb-1">
                          Drop a file here, or click to select
                        </p>
                        <p className="text-neutral-grey-400 text-sm">
                          Photos: JPG, PNG, WEBP, HEIC (max 25MB)
                        </p>
                        <p className="text-neutral-grey-400 text-sm">
                          Videos: MP4, WebM, MOV, AVI, MKV (max 100MB, max 3 min)
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                {formErrors.file && (
                  <p className="mt-2 text-crimson text-sm">{formErrors.file}</p>
                )}
              </div>

              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={!guestName.trim() || !selectedFile}
                className="w-full py-4 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg shadow-lg shadow-gold/30"
              >
                Upload File
              </button>
            </div>
          )}

          {/* Uploading State */}
          {uploadState === UPLOAD_STATES.UPLOADING && (
            <div className="py-12 text-center">
              <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-gold animate-spin" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                Uploading Your File...
              </h3>
              <p className="text-neutral-grey-300 mb-8">
                Please don't close this window
              </p>

              {/* Progress Bar */}
              <div className="w-full bg-neutral-grey-800 rounded-full h-3 mb-4">
                <div
                  className="bg-gold h-3 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-gold font-semibold">{uploadProgress}%</p>
            </div>
          )}

          {/* Success State */}
          {uploadState === UPLOAD_STATES.SUCCESS && (
            <div className="py-12 text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                Upload Successful! 🎉
              </h3>
              <p className="text-neutral-grey-300 mb-8">
                Your file has been added to the gallery. Thank you for sharing your memory!
              </p>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleReset}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all"
                >
                  Upload Another
                </button>
                <button
                  onClick={handleClose}
                  className="px-6 py-3 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all shadow-lg shadow-gold/30"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Error State */}
          {uploadState === UPLOAD_STATES.ERROR && (
            <div className="py-12 text-center">
              <div className="w-20 h-20 bg-crimson/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-crimson" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                Upload Failed
              </h3>
              <p className="text-neutral-grey-300 mb-8">
                {errorMessage}
              </p>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleReset}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all"
                >
                  Start Over
                </button>
                <button
                  onClick={() => setUploadState(UPLOAD_STATES.FORM)}
                  className="px-6 py-3 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all shadow-lg shadow-gold/30"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default GuestUploadModal;
